<template>
  <v-container fluid class="pt-4">
    <div v-if="value">
      <v-row>
        <v-col sm="3" cols="12">
          <v-select
            v-model="computedValue.source"
            :items="sourceSchema.source.enum"
            :label="sourceSchema.source.title"
            :hint="sourceSchema.source.tooltip"
            persistent-hint
            dense
            @change="$emit('change')"
        /></v-col>
        <v-col sm="3" cols="12" v-if="value.source === 'Market'">
          <v-select
            label="Market Name"
            v-model="computedValue.market_name"
            :items="availableMarkets"
            @change="$emit('change')"
            dense
          />
        </v-col>
        <v-col sm="3" cols="12" v-if="submarketType === 'reserve' && value.source === 'Market'">
          <v-select
            v-model="computedValue.index_type"
            :items="sourceSchema.index_type.enum"
            :label="sourceSchema.index_type.title"
            :hint="sourceSchema.index_type.tooltip"
            persistent-hint
            dense
            @change="$emit('change')"
          >
            <template #selection="data">
              {{ data.item | replaceDash }}
            </template>
            <template #item="data">
              {{ data.item | replaceDash }}
            </template>
          </v-select>
        </v-col>
        <v-col
          sm="6"
          cols="12"
          v-if="!['Custom', 'IHR'].includes(value.source) && submarketType !== 'reserve'"
        >
          <InputMarketNode
            v-if="showNodeData"
            v-model="computedMarketNode"
            :market="value.market_name"
            :index-type="value.index_type"
            :key="`node-dd-${nodeDropdownKey}`"
            @change="$emit('change')"
            dense
          ></InputMarketNode>
          <v-text-field
            v-else
            v-model.number="computedValue.node_id"
            :label="sourceSchema.node_id.title"
            :hint="sourceSchema.node_id.tooltip"
            dense
            @change="$emit('change')"
          />
        </v-col>
        <template v-if="value.source === 'IHR'">
          <v-col sm="5" cols="12">
            <FeathersAutocomplete
              v-model="computedValue.node_id"
              label="Forecast ID"
              service-name="forecasts"
              item-text="id"
              :params="forecastsParams"
            >
              <template #item="data">
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.id }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    formatForecastSubtitle(data.item)
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </FeathersAutocomplete>
          </v-col>
          <v-col sm="4" cols="12">
            <v-select
              v-model="computedValue.ihr_type"
              label="Forecast Type"
              :items="forecastTypeItems"
            ></v-select>
          </v-col>
        </template>
      </v-row>
      <v-row dense v-if="value.source === 'Custom'">
        <v-col sm="4" cols="12">
          <v-select
            v-model="computedValue.price_unit"
            :items="sourceSchema.price_unit.enum"
            :label="sourceSchema.price_unit.title"
            :hint="sourceSchema.price_unit.tooltip"
            @change="$emit('change')"
          />
        </v-col>
        <v-col sm="4" cols="12">
          <v-select
            v-model="computedValue.energy_unit"
            :items="sourceSchema.energy_unit.enum"
            :label="sourceSchema.energy_unit.title"
            :hint="sourceSchema.energy_unit.tooltip"
            @change="$emit('change')"
          /> </v-col
        ><v-col sm="4" cols="12">
          <v-select
            v-model="computedValue.power_unit"
            :items="sourceSchema.power_unit.enum"
            :label="sourceSchema.power_unit.title"
            :hint="sourceSchema.power_unit.tooltip"
            @change="$emit('change')"
          />
        </v-col>
      </v-row>
      <InputYearlyProfile
        v-if="value.source === 'Custom'"
        v-model="computedValue.profile"
        :profile-type="sourceSchema.profile.profile_type"
        :label="sourceSchema.profile.title"
        :hint="sourceSchema.profile.tooltip"
        :num_years="num_years"
        :multi-year-upload="true"
        object-value
        @change="$emit('change')"
      />
      <InputYearlyProfile
        v-if="source === 'Custom'"
        v-model="performancePrice"
        :profile-type="sourceSchema.performance_price.profile_type"
        :label="sourceSchema.performance_price.title"
        :hint="sourceSchema.performance_price.tooltip"
        :num_years="num_years"
        :multi-year-upload="true"
        nullable
        object-value
        @change="$emit('change')"
      />
      <v-select
        v-if="value.source === 'Market' && submarketType !== 'reserve'"
        v-model="computedValue.index_type"
        :items="sourceSchema.index_type.enum"
        :label="sourceSchema.index_type.title"
        :hint="sourceSchema.index_type.tooltip"
        persistent-hint
        @change="$emit('change')"
      >
        <template #selection="data">
          {{ data.item | replaceDash }}
        </template>
        <template #item="data">
          {{ data.item | replaceDash }}
        </template>
      </v-select>
      <v-row v-if="submarketType === 'reserve' && source === 'Market'">
        <v-col>
          <v-autocomplete
            v-model="computedValue.ancillary_price_type"
            label="Ancillary Price Type"
            :items="ancillaryPriceTypeItems"
            dense
            @change="$emit('change')"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12">
          <v-autocomplete
            v-model="computedValue.node_id"
            label="Zone/Region ID"
            :items="ancillaryPriceIds"
            dense
            @change="$emit('change')"
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-text-field
        v-if="value.source === 'PC'"
        v-model.number="computedValue.index_type"
        :label="sourceSchema.index_type.title"
        :hint="sourceSchema.index_type.tooltip"
        type="Number"
        @change="$emit('change')"
      />
      <v-card v-if="showNodeData">
        <v-card-text>
          <v-row dense>
            <v-col v-if="showNodeMap" cols="12" md="5" class="ml-4">
              <div :style="`height: 200px`">
                <LMap
                  :zoom="5"
                  :center="projectCoordinates"
                  :bounds="mapBounds"
                  style="height: 100%; width: 100%; z-index: 0"
                  :options="{ attributionControl: false }"
                  ref="map"
                  :key="`nodeMap-${nodeMapKey}`"
                >
                  <LTileLayer :url="mapUrl" />
                  <LMarker
                    v-if="projectCoordinates"
                    :lat-lng="projectCoordinates"
                    :icon="projectIcon"
                  />
                  <LMarker v-if="nodeCoordinates" :lat-lng="nodeCoordinates" :icon="nodeIcon">
                    <LPopup :options="{ offset: [0, -24] }">
                      <div>
                        {{ `${marketNode.Name} - ${marketNode.PTID}` }}
                      </div>
                    </LPopup>
                  </LMarker>
                  <LPolyline v-if="nodeCoordinates" :lat-lngs="polylineLatLngs"></LPolyline>
                </LMap>
              </div>
              <p>Distance from project: {{ nodeProjectDistance }} km</p>
            </v-col>
            <v-divider v-if="showNodeMap" vertical class="mx-2"></v-divider>
            <v-col
              class="text-center"
              cols="12"
              :key="`priceChart-${nodeMapKey}`"
              v-if="!disablePriceData"
            >
              <v-btn
                v-if="showChartBtn"
                text
                outlined
                color="primary"
                :loading="isFindPriceServicePending"
                @click="loadPriceChart = !loadPriceChart"
                >Display Prices</v-btn
              >
              <Plotly v-else :layout="priceChartLayout" :data="priceChartData" />
            </v-col>
            <v-col v-else class="text-center" cols="12">
              <p>No price data</p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-card v-if="submarketType === 'reserve' && source === 'Market'">
        <v-card-text>
          <p>
            <span class="font-weight-medium">Available Years: </span>
            <span>{{ ancillaryAvailableYears }}</span>
          </p>
        </v-card-text>
      </v-card>
      <v-divider class="my-4" />
      <InputYearlyProfile
        v-model="computedValue.availability"
        profile-type="AVAILABILITY"
        :label="sourceSchema.availability.title"
        :hint="sourceSchema.availability.tooltip"
        :num_years="num_years"
        :multi-year-upload="true"
        nullable
        object-value
        @change="$emit('change')"
      />
      <v-row>
        <v-col sm="4" cols="12">
          <v-text-field
            v-model.number="computedValue.market_friction"
            :label="sourceSchema.market_friction.title"
            :suffix="sourceSchema.market_friction.unit"
            :hint="sourceSchema.market_friction.tooltip"
            type="Number"
            @change="$emit('change')"
          />
        </v-col>
        <v-col sm="4" cols="12">
          <v-text-field
            v-model.number="computedValue.bid_adder"
            :suffix="sourceSchema.bid_adder.unit"
            :label="sourceSchema.bid_adder.title"
            :hint="sourceSchema.bid_adder.tooltip"
            type="Number"
            @change="$emit('change')"
          />
        </v-col>
        <v-col sm="4" cols="12">
          <v-text-field
            v-model.number="computedValue.transport_tariff"
            :label="sourceSchema.transport_tariff.title"
            :suffix="sourceSchema.transport_tariff.unit"
            :hint="sourceSchema.transport_tariff.tooltip"
            type="Number"
            @change="$emit('change')"
          />
        </v-col>
      </v-row>
      <InputYearly
        v-model="computedValue.vom_purchases"
        :label="sourceSchema.vom_purchases.title"
        :hint="sourceSchema.vom_purchases.tooltip"
        :suffix="sourceSchema.vom_purchases.unit"
        :num_years="num_years"
        :year_start="year_start"
        fill
        :fill-value="sourceSchema.vom_purchases.items.default"
        @change="$emit('change')"
      />
      <InputYearly
        v-model="computedValue.vom_sales"
        :label="sourceSchema.vom_sales.title"
        :suffix="sourceSchema.vom_sales.unit"
        :hint="sourceSchema.vom_sales.tooltip"
        :num_years="num_years"
        :year_start="year_start"
        fill
        :fill-value="sourceSchema.vom_sales.items.default"
        @change="$emit('change')"
      />
      <v-switch
        v-model="computedValue.buy_limits"
        :label="sourceSchema.buy_limits.title"
        hint="Limit energy import over the period or each interval"
        persistent-hint
        @change="$emit('change')"
      />
      <v-switch
        v-model="computedValue.sell_limits"
        :label="sourceSchema.sell_limits.title"
        hint="Limit energy export over the period or each interval"
        persistent-hint
        @change="$emit('change')"
      />
      <InputYearly
        v-for="limit in limitsForRender"
        :key="limit[0]"
        :suffix="limit[1].unit"
        v-model="computedValue[limit[0]]"
        :label="limit[1].title"
        :hint="limit[1].tooltip"
        :num_years="num_years"
        @change="$emit('change')"
      />
    </div>
    <v-snackbar v-model="snackbar" app right :color="snackbarInfo.color" timeout="8000">
      {{ snackbarInfo.message }}

      <v-btn color="white" dark icon @click="snackbar = false" small>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import marketSettings from "@/mixins/marketsSettings";
import { CaseModel, schemas } from "data-dictionary";
import { makeFindMixin } from "feathers-vuex";
import { divIcon, latLng, latLngBounds } from "leaflet";
import { DateTime } from "luxon";
import { LMap, LMarker, LPolyline, LPopup, LTileLayer } from "vue2-leaflet";
import { mapActions } from "vuex";

export default {
  name: "SystemProjectSubMarket",
  components: {
    FeathersAutocomplete: () => import("@/components/FeathersAutocomplete.vue"),
    InputMarketNode: () => import("@/components/InputMarketNode.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    LMap,
    LMarker,
    LTileLayer,
    LPolyline,
    LPopup,
    Plotly: () => import("@/components/charts/Plotly.vue"),
  },
  mixins: [
    makeFindMixin({
      service() {
        return this.priceServiceName;
      },
      name: "priceService",
      watch: true,
      queryWhen: "priceServiceQueryWhen",
      items: "pricesData",
    }),
    makeFindMixin({
      service: "data-services/ancillary-inventory",
      name: "ancillaryInventory",
      watch: true,
      items: "ancillaryInventoryItems",
    }),
  ],
  filters: { replaceDash: (v) => v.replace("-", " ") },
  props: {
    value: { type: [Object, null], required: false },
    num_years: { type: Number, required: true },
    year_start: { type: Number, required: true },
    errors: {
      type: Object,
      required: false,
    },
    toggleable: { type: Boolean, required: false, default: false },
    submarketType: { type: String, required: false, default: "" },
    projectCoordinates: { type: Array, required: false, default: undefined },
    open: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      CaseModel,
      schema: schemas.filter((v) => v.$id === "submarket.schema.json")[0],
      mapUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      projectIcon: divIcon({
        className: "my-custom-pin",
        html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#306d8a" stroke="#90d7ec"/>
                <g transform="scale(0.7 0.7) translate(4.5 2.5)">
                <path d="M11.48 20v-6.26H8L13 4v6.26h3.35L11.48 20z" fill="white"/>
                </g>
              </svg>`,
        iconAnchor: [24, 44],
      }),
      nodeIcon: divIcon({
        className: "my-custom-pin",
        html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#306d8a" stroke="#90d7ec"/>
                <g transform="scale(0.7 0.7) translate(4.5 2.5)">
                <text x="52%" y="70%" text-anchor="middle" fill="#fff" font-size="medium" font-family="sans-serif">$</text>
                </g>
              </svg>`,
        iconAnchor: [24, 44],
      }),
      nodeMapKey: 1,
      marketNode: {},
      nodeProjectDistance: 0,
      availableMarkets: [],
      loadPriceChart: false,
      nodeDropdownKey: 1,
      forecastsParams: {
        query: {
          $sort: { id: -1 },
          $select: ["id", "type", "input", "status"],
          status: "DONE",
        },
      },
      forecastTypeItems: [
        { text: "Base Forecast", value: "base" },
        { text: "High Forecast", value: "high" },
        { text: "Low Forecast", value: "low" },
      ],
      snackbar: false,
      snackbarInfo: {},
      ancillaryInventory: {
        ancillaryPrice: null,
        priceType: null,
        id: null,
      },
    };
  },
  computed: {
    currentUser() {
      return this.$auth.user;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
    source() {
      return this.value ? this.value.source : null;
    },
    performancePrice: {
      get() {
        return this.value ? this.value.performance_price : null;
      },
      set(val) {
        const valueCopy = { ...this.value, performance_price: val };
        this.$emit("input", valueCopy);
        this.$emit("change");
      },
    },
    buyLimitProperties() {
      const buyLimitProperties = this.schema.allOf.filter((v) =>
        v.if.properties.buy_limits
          ? v.if.properties.buy_limits.const === this.value.buy_limits
          : false
      );
      return buyLimitProperties.length ? buyLimitProperties[0].then.properties : {};
    },
    sellLimitProperties() {
      const sellLimitProperties = this.schema.allOf.filter((v) =>
        v.if.properties.sell_limits
          ? v.if.properties.sell_limits.const === this.value.sell_limits
          : false
      );
      return sellLimitProperties.length ? sellLimitProperties[0].then.properties : {};
    },
    limitsForRender() {
      const reducedbuyLimitProperties = Object.keys(this.buyLimitProperties).length
        ? (({ interval_buy_limits, horizon_buy_limits }) => ({
            interval_buy_limits,
            horizon_buy_limits,
          }))(this.buyLimitProperties)
        : {};
      const reducedsellLimitProperties = Object.keys(this.sellLimitProperties).length
        ? (({ interval_sell_limits, horizon_sell_limits }) => ({
            interval_sell_limits,
            horizon_sell_limits,
          }))(this.sellLimitProperties)
        : {};
      const limits = [];
      const length = Math.max(
        Object.entries(reducedbuyLimitProperties).length,
        Object.entries(reducedsellLimitProperties).length
      );
      for (let index = 0; index < length; index += 1) {
        if (Object.entries(reducedbuyLimitProperties)[index]) {
          limits.push(Object.entries(reducedbuyLimitProperties)[index]);
        }
        if (Object.entries(reducedsellLimitProperties)[index]) {
          limits.push(Object.entries(reducedsellLimitProperties)[index]);
        }
      }

      return limits;
    },
    sourceSchema() {
      const commonProperties = this.schema.properties;
      const sourceProperties = this.schema.allOf.filter((v) =>
        v.if.properties.source ? v.if.properties.source.const === this.value.source : false
      )[0].then.properties;

      return {
        ...commonProperties,
        ...sourceProperties,
        ...this.buyLimitProperties,
        ...this.sellLimitProperties,
      };
    },
    nodeCoordinates() {
      return this.marketNode && this.marketNode.Coordinates
        ? [this.marketNode.Coordinates.y, this.marketNode.Coordinates.x]
        : null;
    },
    mapBounds() {
      if (this.nodeCoordinates) {
        const bounds = latLngBounds([this.projectCoordinates, this.nodeCoordinates]);
        return bounds.pad(0.4);
      }
      return null;
    },
    polylineLatLngs() {
      if (this.nodeCoordinates) {
        return [this.projectCoordinates, this.nodeCoordinates];
      }
      return null;
    },
    displayDivider() {
      return this.$vuetify.breakpoint.smAndUp;
    },
    computedMarketNode: {
      get() {
        return this.marketNode;
      },
      set(val) {
        this.marketNode = val;
        const valueCopy = this.value;
        valueCopy.node_id = val[this.currentMarketSettings.nodeKey] || null;
        this.$emit("input", valueCopy);
        this.$emit("change");
        this.loadPriceChart = false;
      },
    },
    currentMarket() {
      return this.value && this.value.source === "Market" ? this.value.market_name : null;
    },
    currentMarketSettings() {
      if (!this.currentMarket) return {};
      if (this.currentMarket === "ERCOT") {
        return marketSettings[this.currentMarket][this.value.index_type];
      }
      return this.currentMarket ? marketSettings[this.currentMarket] : {};
    },
    currentIndexType() {
      return this.value && this.value.source === "Market" ? this.value.index_type : null;
    },
    showNodeData() {
      return (
        this.value &&
        this.value.source === "Market" &&
        ["CAISO", "CHILE", "ERCOT", "ISONE", "MISO", "NYISO", "PJM", "SPP"].includes(
          this.value.market_name
        ) &&
        this.submarketType !== "reserve"
      );
    },
    showNodeMap() {
      return this.value && this.value.source === "Market" && this.value.market_name === "NYISO";
    },
    priceServiceName() {
      if (this.showNodeData) {
        const keyMap = {
          "Real-Time": "realTimeService",
          "Day-Ahead": "dayAheadService",
        };
        return this.currentMarketSettings[keyMap[this.value.index_type]];
      }
      return "";
    },
    showChartBtn() {
      return this.isFindPriceServicePending || !this.pricesData.length;
    },
    disablePriceData() {
      return this.currentMarket === "MISO" && this.value.index_type === "Day-Ahead";
    },
    priceServiceQueryWhen() {
      return this.loadPriceChart && this.showNodeData;
    },
    priceServiceParams() {
      return {
        query: {
          [this.currentMarketSettings.nodeKey]: this.value ? this.value.node_id : "",
        },
      };
    },
    priceServiceFetchParams() {
      return {
        query: {
          [this.currentMarketSettings.nodeKey]: this.value ? this.value.node_id : "",
          downsample: "720 minutes",
          $and: [
            {
              [this.currentMarketSettings.dateKey]: {
                $gt: `${this.year_start}-01-01T00:00:00.000Z`,
              },
            },
            {
              [this.currentMarketSettings.dateKey]: {
                $lt: `${this.year_start + 1}-01-01T00:00:00.000Z`,
              },
            },
          ],
          $limit: 750,
        },
      };
    },
    priceChartData() {
      const keyMap = {
        "Real-Time": "realTimePriceKey",
        "Day-Ahead": "dayAheadPriceKey",
      };
      return this.pricesData.length
        ? [
            {
              x: this.pricesData[0].time_window,
              y: this.pricesData[0][
                this.currentMarketSettings[keyMap[this.value.index_type]] ||
                  this.currentMarketSettings.priceKey
              ],
            },
          ]
        : [];
    },
    priceChartLayout() {
      return {
        height: 240,
        margin: { t: 20, b: 10, r: 25, l: 45 },
        yaxis: {
          title: "$/MWh",
        },
        xaxis: {
          autorange: true,
          rangeslider: {},
          type: "date",
        },
      };
    },
    ancillaryInventoryParams() {
      return {
        query: {
          "Data Provider": this.computedValue.market_name,
          Market: this.computedValue.index_type,
          $limit: 100,
        },
      };
    },
    ancillaryPriceTypeItems() {
      return this.ancillaryInventoryItems.map((v) => ({
        text: v["Ancillary Price Type"],
        value: v["Ancillary Price Type"],
      }));
    },
    ancillaryPriceIds() {
      return this.computedValue.ancillary_price_type
        ? this.ancillaryInventoryItems.find(
            (v) => v["Ancillary Price Type"] === this.computedValue.ancillary_price_type
          ).IDs
        : [];
    },
    ancillaryAvailableYears() {
      return this.computedValue.ancillary_price_type
        ? this.ancillaryInventoryItems
            .find((v) => v["Ancillary Price Type"] === this.computedValue.ancillary_price_type)
            ["Years Available"].sort()
            .join(", ")
        : "";
    },
  },
  watch: {
    source(val) {
      if (val === "Custom") {
        const valueCopy = this.value;
        valueCopy.profile = [];
        valueCopy.energy_prices = [];
        valueCopy.performance_price = null;
        if (!this.value.price_unit) valueCopy.price_unit = this.sourceSchema.price_unit.default;
        if (!this.value.energy_unit) valueCopy.energy_unit = this.sourceSchema.energy_unit.default;
        if (!this.value.power_unit) valueCopy.power_unit = this.sourceSchema.power_unit.default;
        this.$emit("input", valueCopy);
        this.$emit("change");
      }
    },
    currentIndexType() {
      if (this.value.source === "Market" && this.currentMarket === "SPP") {
        const valueCopy = this.value;
        valueCopy.node_id = null;
        this.marketNode = {};
        this.computedValue = valueCopy;
      }
      if (this.value.source === "Market" && this.currentMarket === "ERCOT") {
        const valueCopy = this.value;
        valueCopy.node_id = null;
        this.marketNode = {};
        this.computedValue = valueCopy;
        this.nodeDropdownKey += 1;
      }
    },
    nodeCoordinates(val) {
      if (val) {
        const distance = this.$refs.map.mapObject.options.crs.distance(
          latLng(this.nodeCoordinates),
          latLng(this.projectCoordinates)
        );
        this.nodeProjectDistance = (distance / 1000).toFixed(2);
        this.nodeMapKey += 1;
      }
    },
    open(val) {
      if (val) {
        this.nodeMapKey += 1;
      }
    },
    currentMarket(v) {
      const valueCopy = this.value;
      if (this.submarketType === "reserve") {
        valueCopy.ancillary_price_type = null;
      } else {
        this.nodeDropdownKey += 1;
        valueCopy.node_id = null;
        if (["CHILE"].includes(v)) {
          valueCopy.index_type = "Day-Ahead";
        }
      }
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
    priceServiceName() {
      this.loadPriceChart = false;
    },
    priceServiceQueryWhen() {
      this.findPriceService().then((r) => {
        if (!r.data.length) {
          this.snackbar = true;
          this.snackbarInfo = {
            color: "info",
            message: `No results for id: '${this.value.node_id}' for year ${this.year_start}`,
          };
        }
      });
    },
    isFindPriceServicePending(v) {
      if (!v) {
        // After find
        this.loadPriceChart = false;
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (this.value && this.value.node_id && this.value.source === "Market") {
      if (this.submarketType === "reserve") {
        this.computedValue.ancillary_price_type = null;
      } else {
        this.findNodes(
          { query: { [this.currentMarketSettings.nodeKey]: this.value.node_id } },
          this.currentMarketSettings.nodeService
        ).then((r) => {
          if (r.total) {
            [this.marketNode] = r.data;
            this.nodeMapKey += 1;
          }
        });
      }
    }
    this.getUser(this.currentUser.id).then((user) => {
      this.availableMarkets = user.dataLicenses.map((l) => l.name);
    });
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      findNodes(dispatch, payload, path) {
        return dispatch(`${path}/find`, payload);
      },
      getUser: "users/getAcelerexUser",
    }),
    formatNodeItem(item) {
      return `${item[this.currentMarketSettings.nodeName]} - ${
        item[this.currentMarketSettings.nodeKey]
      }`;
    },
    formatForecastSubtitle(item) {
      const { input } = item;
      const { settings } = input;
      const market = input.energy_ancillary_inputs
        ? input.energy_ancillary_inputs.market_name || ""
        : "";
      const marketNode = market ? input.energy_ancillary_inputs.node_id : "";
      const startDate =
        settings && settings.start_of_forecast_date
          ? DateTime.fromISO(settings.start_of_forecast_date, { zone: "utc" }).toLocaleString(
              DateTime.DATETIME_MED
            )
          : "";
      const endDate =
        settings && settings.end_of_forecast_date
          ? DateTime.fromISO(settings.end_of_forecast_date, { zone: "utc" }).toLocaleString(
              DateTime.DATETIME_MED
            )
          : "";
      return [market, marketNode, startDate, endDate].every((v) => !v)
        ? ""
        : `${market} - ${marketNode} (${startDate} - ${endDate})`;
    },
  },
};
</script>

<style></style>
